import React, { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardBody, Container } from 'reactstrap';

import CenteredLoader from '../../components/CenteredLoader/CenteredLoader';
import ExpandableText from '../../components/ExpandableText/ExpandableText';
import GolfCourses from '../../components/GolfCourses/GolfCourses';
import Helmet from '../../components/common/Helmet';
import Map from '../../containers/Map';
import { formatISODate } from '../../helpers/formatISODate';
import { sortPromotedGolfCourses } from '../../helpers/sortPromotedGolfCourses';
import { useDatesFilter } from '../../hooks/useDatesFilter';
import usePopularGolfCourses from '../../hooks/usePopularGolfCourses';
import useTranslate from '../../hooks/useTranslate';
import { MappedCountryType } from '../../services/apis/countries/types';
import useSearchData from '../../services/apis/search/useSearchData';
import countryDescriptions from './descriptions';

const DESCRIPTION_CLAMP = 2;

interface CountryDescriptionPropsType {
  country: MappedCountryType;
  coursesAmount: number;
  className: string;
}

const CountryDescription: FC<CountryDescriptionPropsType> = props => {
  const { country, coursesAmount, className } = props;
  const description = country.code && countryDescriptions[country.code];
  const { translate } = useTranslate();

  return (
    <>
      {description && (
        <div className={className}>
          <h1>{translate(description.title)}</h1>
          <ExpandableText
            text={description.text.map(translationKey => (
              <p key={translationKey}>{translate(translationKey, { amount: coursesAmount })}</p>
            ))}
            clamp={DESCRIPTION_CLAMP}
          />
        </div>
      )}
    </>
  );
};

const CountryPage: FC = () => {
  const params = useParams();
  const countryName = params.country;
  const { startDate } = useDatesFilter();
  const selectedDate = useMemo(() => formatISODate(startDate), [startDate]);
  const { golfCourses, countries, districts, isLoading } = useSearchData();
  const { translate } = useTranslate();
  const country = countries.find(c => c.name === countryName);
  const mapCourses = golfCourses?.map(g => ({
    ...g,
    marked: g.country.id === country?.id,
  }));
  const countryCourses = useMemo(() => {
    const countryCourses =
      golfCourses?.filter(golfCourse => golfCourse.country.id === country?.id) ?? [];

    return sortPromotedGolfCourses(countryCourses);
  }, [golfCourses, country]);
  const nrOfCourses = countryCourses?.length || 0;
  const popularGolfCourses = usePopularGolfCourses();

  return (
    <>
      <Helmet
        titleTranslationKey="ClubListContainer.Title"
        titleData={{
          name: countryName,
          amount: nrOfCourses,
        }}
        descriptionTranslationKey="ClubListContainer.helmetDescription"
        descriptionData={{
          name: countryName,
          amount: nrOfCourses,
        }}
      />
      {!isLoading && mapCourses && (
        <>
          <div className="mt-2 mb-5">
            <Map courses={mapCourses} hoveredCourse={undefined} showMap />
          </div>
          <Container className="mb-5">
            {country && (
              <CountryDescription className="mb-5" coursesAmount={nrOfCourses} country={country} />
            )}

            {country &&
              !!countryCourses?.length &&
              districts
                .filter(district => district.countryId === country.id)
                .map(district => (
                  <Card key={district.id} className="mb-4">
                    <CardBody>
                      <h2 className="fw-bold">{district.name}</h2>
                      <GolfCourses
                        golfCourses={countryCourses.filter(
                          course => course.district.id === district.id
                        )}
                        selectedDate={selectedDate}
                        topRatedGolfCourses={
                          popularGolfCourses.find(item => item.country.id === country.id)?.topRated
                        }
                        topBookedGolfCourses={
                          popularGolfCourses.find(item => item.country.id === country.id)?.topBooked
                        }
                      />
                    </CardBody>
                  </Card>
                ))}
          </Container>
        </>
      )}

      {isLoading && <CenteredLoader message={String(translate('Courses.LoadingGolfCourses'))} />}
    </>
  );
};

export default CountryPage;
